import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { PlusCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAdditionalPayments } from '../../../provider/profesor/getAdditionalPayments'; // Adjust the import path as necessary
import { crearAdditionalPayments } from '../../../provider/profesor/crearAdditionalPayments'; // Adjust the import path as necessary
const AdditionalPaymentsDialog = ({ isOpen, onClose, teacherId, date }) => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        description: '',
        amount: '',
        date: new Date().toISOString().slice(0, 16)
    });

    // Fetch additional payments
    const fetchPayments = async () => {
        if (!teacherId) return;

        try {
            setLoading(true);
            setPayments(await getAdditionalPayments(date.year, date.month ));
        } catch (error) {
            console.error('Error fetching payments:', error);
            toast.error('Error fetching additional payments');
        } finally {
            setLoading(false);
        }
    };

    // Create new payment
    const createPayment = async (e) => {
        e.preventDefault();

        if (!formData.description || !formData.amount) {
            toast.error('Please fill in all required fields');
            return;
        }

        try {
            setLoading(true);
            const paymentData = {
                teacherId: parseInt(teacherId),
                date: "2025-06-20T10:00:00Z", //borrar
                description: formData.description,
                amount: parseNumber(formData.amount)
            };
            await crearAdditionalPayments(paymentData); 
        } catch (error) {
            console.error('Error creating payment:', error);
            toast.error('Error creating additional payment');
        } finally {
            setFormData({
                description: '',
                amount: '',
                date: new Date().toISOString().slice(0, 16)
            });
            fetchPayments();
            setShowCreateForm(false);
            setLoading(false);
        }
    };

    // Calculate total
    const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);

    useEffect(() => {
        if (isOpen && teacherId) {
            fetchPayments();
        }
    }, [isOpen, teacherId]);
        const formatNumber = (value) => {
        if (!value) return "";
        // Remove non-digit characters
        const number = value.toString().replace(/\D/g, "");
        // Format with thousands separator
        return new Intl.NumberFormat('es-CO').format(number);
    };
    const parseNumber = (formattedValue) => {
        if (!formattedValue) return 0;
        return parseInt(formattedValue.replace(/\D/g, ""), 10);
    };


    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Additional Payments</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Create Payment Form */}
                    {showCreateForm && (
                        <div className="bg-gray-50 p-4 rounded-lg border">
                            <h3 className="text-lg font-semibold mb-4">Create New Additional Payment</h3>
                            <form onSubmit={createPayment} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Description *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            placeholder="Payment description..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount (COP) *
                                        </label>
                                        <input
                                            type="character"                                            
                                            value={formData.amount}
                                            onChange={(e) => setFormData({ ...formData, amount: formatNumber(e.target.value) })}
                                            placeholder="0.00"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button type="submit" disabled={loading}>
                                        {loading ? 'Creating...' : 'Create Payment'}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowCreateForm(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold">Payment History</h3>
                        {!showCreateForm && (
                            <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
                                <PlusCircle className="w-4 h-4" />
                                Add Payment
                            </Button>
                        )}
                    </div>

                    {/* Payments Table */}
                    {loading ? (
                        <div className="text-center py-8">Loading...</div>
                    ) : (
                        <div className="border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>ID</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {payments.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                                                No additional payments found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        payments.map((payment) => (
                                            <TableRow key={payment.id}>
                                                <TableCell>{payment.id}</TableCell>
                                                <TableCell>
                                                    {new Date(payment.date).toLocaleString("en", {
                                                        year: "numeric",
                                                        month: "short",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </TableCell>
                                                <TableCell>{payment.description}</TableCell>
                                                <TableCell className="text-right">
                                                    {new Intl.NumberFormat("es-CO", {
                                                        style: "currency",
                                                        currency: "COP",
                                                    }).format(payment.amount)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                    {payments.length > 0 && (
                                        <TableRow className="bg-gray-50 font-semibold">
                                            <TableCell colSpan={3} className="text-right">Total:</TableCell>
                                            <TableCell className="text-right">
                                                {new Intl.NumberFormat("es-CO", {
                                                    style: "currency",
                                                    currency: "COP",
                                                }).format(totalAmount)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AdditionalPaymentsDialog;